import { useState, useEffect, useRef } from 'react'
import { codeSnippets, searchSnippets, getAvailableGroups, getGroupMetadata } from '../data/codeSnippets'

/**
 * Elegante Snippets Modal estilo IDE
 *
 * Exibe snippets de código disponíveis com busca e preview.
 * Ao clicar em um snippet, insere o código no editor.
 */
export default function SnippetsModal({ isOpen, onClose, onSelect }) {
  const [search, setSearch] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [filteredSnippets, setFilteredSnippets] = useState(codeSnippets)
  const [expandedIndex, setExpandedIndex] = useState(null)
  const searchInputRef = useRef(null)
  const listRef = useRef(null)
  const availableGroups = getAvailableGroups()

  // Update filtered snippets when search or group filter changes
  useEffect(() => {
    let results = search.trim() === '' ? codeSnippets : searchSnippets(search)

    // Apply group filter
    if (selectedGroup) {
      results = results.filter(snippet => snippet.group === selectedGroup)
    }

    setFilteredSnippets(results)
    setSelectedIndex(0)
  }, [search, selectedGroup])

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0)
    }
  }, [isOpen])

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev + 1) % filteredSnippets.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) =>
          prev === 0 ? filteredSnippets.length - 1 : prev - 1
        )
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (filteredSnippets[selectedIndex]) {
          onSelect(filteredSnippets[selectedIndex])
          onClose()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, selectedIndex, filteredSnippets, onSelect, onClose])

  // Auto-scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const items = listRef.current.querySelectorAll('[data-snippet-item]')
      if (items[selectedIndex]) {
        items[selectedIndex].scrollIntoView({ block: 'nearest' })
      }
    }
  }, [selectedIndex])

  if (!isOpen) return null

  const selectedSnippet = filteredSnippets[selectedIndex]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#252526] border border-[#3c3c3c] rounded-lg shadow-2xl w-full max-w-4xl mx-4 flex flex-col h-[560px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ─── Header ─── */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#3c3c3c] flex-shrink-0 bg-[#1e1e1e]/50">
          <div className="flex items-center gap-2">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0078d4"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="16 18 22 12 16 6"/>
              <polyline points="8 6 2 12 8 18"/>
            </svg>
            <span className="text-sm font-semibold text-[#cccccc]">Code Snippets</span>
          </div>
          <button
            onClick={onClose}
            className="text-[#555] hover:text-white transition"
            aria-label="Close"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* ─── Search Input ─── */}
        <div className="px-5 py-3 border-b border-[#3c3c3c] flex-shrink-0">
          <div className="relative">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="absolute left-2.5 top-1/2 transform -translate-y-1/2"
            >
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search snippets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-[#3c3c3c] text-[#cccccc] text-sm rounded px-3 py-2 pl-8 border border-transparent focus:border-[#0078d4] focus:outline-none transition"
            />
          </div>
          {search && (
            <div className="text-xs text-[#555] mt-1.5">
              {filteredSnippets.length === 0
                ? 'No snippets found'
                : `${filteredSnippets.length} ${filteredSnippets.length === 1 ? 'snippet' : 'snippets'} found`}
            </div>
          )}
        </div>

        {/* ─── Content Area (Groups + List) ─── */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* ─── Group Sidebar ─── */}
          <div className="w-32 border-r border-[#3c3c3c] overflow-y-auto flex flex-col flex-shrink-0">
            <button
              onClick={() => setSelectedGroup(null)}
              className={`px-4 py-3 text-left text-sm font-medium transition text-sm flex-shrink-0 ${
                selectedGroup === null
                  ? 'bg-[#0078d4]/20 border-l-2 border-[#0078d4] text-[#0078d4]'
                  : 'border-l-2 border-transparent text-[#858585] hover:bg-[#2d2d30] hover:text-[#cccccc]'
              }`}
              title="Show all snippets"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="inline mr-2"
              >
                <polyline points="16 18 22 12 16 6"/>
                <polyline points="8 6 2 12 8 18"/>
              </svg>
              All
            </button>
            <div className="border-t border-[#3c3c3c]"></div>
            {availableGroups.map(group => {
              const metadata = getGroupMetadata(group)
              const count = codeSnippets.filter(s => s.group === group).length
              return (
                <button
                  key={group}
                  onClick={() => setSelectedGroup(selectedGroup === group ? null : group)}
                  className={`px-4 py-3 text-left text-sm font-medium transition flex-shrink-0 ${
                    selectedGroup === group
                      ? 'bg-[#0078d4]/20 border-l-2 border-[#0078d4] text-[#0078d4]'
                      : 'border-l-2 border-transparent text-[#858585] hover:bg-[#2d2d30] hover:text-[#cccccc]'
                  }`}
                  title={metadata.description}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="truncate">{metadata.label}</span>
                    <span className="text-[10px] text-[#555] ml-1">{count}</span>
                  </div>
                </button>
              )
            })}
          </div>

          {/* ─── Snippets List ─── */}
          <div
            ref={listRef}
            className="flex-1 border-t border-[#3c3c3c] overflow-y-auto flex flex-col min-h-0"
          >
            {filteredSnippets.length === 0 ? (
              <div className="flex items-center justify-center flex-grow text-[#555] text-sm px-4">
                <div className="text-center">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="mx-auto mb-2 opacity-50"
                  >
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  No snippets found
                </div>
              </div>
            ) : (
              <div className="divide-y divide-[#3c3c3c] flex flex-col">
                {filteredSnippets.map((snippet, idx) => (
                  <div key={snippet.id}>
                    <div
                      className={`w-full px-4 py-3 text-left transition text-sm flex-shrink-0 flex items-center justify-between gap-2 ${
                        idx === selectedIndex
                          ? 'bg-[#0078d4]/20 border-l-2 border-[#0078d4]'
                          : 'hover:bg-[#2d2d30] border-l-2 border-transparent'
                      }`}
                    >
                      <button
                        data-snippet-item
                        onClick={() => {
                          setSelectedIndex(idx)
                        }}
                        className="flex-1 flex items-start gap-2 text-left"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#0078d4"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="flex-shrink-0 mt-0.5"
                        >
                          <polyline points="16 18 22 12 16 6"/>
                          <polyline points="8 6 2 12 8 18"/>
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[#cccccc] truncate">
                            {snippet.name}
                          </div>
                          <div className="text-xs text-[#858585] mt-0.5 line-clamp-1">
                            {snippet.description}
                          </div>
                        </div>
                      </button>

                      {/* Preview Toggle Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setExpandedIndex(expandedIndex === idx ? null : idx)
                        }}
                        className="flex-shrink-0 p-1.5 rounded hover:bg-[#3c3c3c] transition text-[#858585] hover:text-[#cccccc]"
                        title="Preview snippet"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className={`transition-transform ${expandedIndex === idx ? 'rotate-180' : ''}`}
                        >
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </button>

                      {/* Insert Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onSelect(snippet)
                          onClose()
                        }}
                        className="flex-shrink-0 p-1.5 rounded hover:bg-[#0078d4] transition text-[#0078d4] hover:text-white"
                        title="Insert snippet"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="12 5 19 12 12 19"/>
                          <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                      </button>
                    </div>

                    {/* ─── Expandable Preview ─── */}
                    {expandedIndex === idx && (
                      <div className="bg-[#1e1e1e]/50 border-t border-[#3c3c3c] px-4 py-3 text-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-block px-2 py-1 bg-[#0078d4]/20 text-[#0078d4] text-xs font-medium rounded">
                            {snippet.category}
                          </span>
                          {snippet.shortcut && (
                            <span className="inline-block px-2 py-1 bg-[#3c3c3c] text-[#858585] text-[10px] font-mono rounded">
                              {snippet.shortcut}
                            </span>
                          )}
                        </div>
                        <pre
                          className="text-xs font-mono text-[#ce9178] bg-[#252526] p-3 rounded border border-[#3c3c3c] overflow-x-auto whitespace-pre-wrap break-words max-h-[200px] overflow-y-auto"
                          style={{
                            fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                          }}
                        >
                          <code>{snippet.code}</code>
                        </pre>
                        {snippet.tags && snippet.tags.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {snippet.tags.map((tag) => (
                              <span
                                key={tag}
                                className="inline-block px-2 py-1 bg-[#3c3c3c]/50 text-[#858585] text-[10px] rounded"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ─── Footer ─── */}
        <div className="px-5 py-3 border-t border-[#3c3c3c] flex-shrink-0 bg-[#1e1e1e]/50 flex items-center justify-between">
          <div className="flex items-center gap-4 text-xs text-[#555]">
            <div className="flex items-center gap-1">
              <span className="inline-block px-1.5 py-0.5 bg-[#3c3c3c] text-[#cccccc] rounded text-[9px] font-mono">
                ↑↓
              </span>
              Navigate
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-block px-1.5 py-0.5 bg-[#3c3c3c] text-[#cccccc] rounded text-[9px] font-mono">
                ↵
              </span>
              Insert
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-block px-1.5 py-0.5 bg-[#3c3c3c] text-[#cccccc] rounded text-[9px] font-mono">
                Esc
              </span>
              Close
            </div>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-1.5 bg-[#3c3c3c] hover:bg-[#454546] text-[#cccccc] text-sm rounded transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
